import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import app from "../firebase";
import * as treatmentService from "../api/firestore/services/treatmentService";

const db = firebase.firestore(app);

/**
 * Gets all planner animals for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of planner animal objects
 * @path Users/{userID}/PlanerAnimals
 */
export async function getAnimals(userID) {
	let animals = [];

	await db
		.collection("Users")
		.doc(userID)
		.collection("PlanerAnimals")
		.get()
		.then(async (snapshot) => {
			snapshot.forEach((node) => {
				let animal = node.data();
				animals.push(animal);
			});
		})
		.catch((e) => {
			throw e;
		});

	return animals;
}

/**
 * Gets all integrated animals for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of integrated animal objects
 * @path Users/{userID}/IntegratedAnimals
 */
export async function getIntegratedAnimals(userID) {
	let animals = [];

	await db
		.collection("Users")
		.doc(userID)
		.collection("IntegratedAnimals")
		.get()
		.then((snapshot) => {
			snapshot.forEach((node) => {
				let animal = node.data();
				animals.push(animal);
			});
		})
		.catch((e) => {
			throw e;
		});

	return animals;
}

/**
 * Gets all non-archived treatments for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of treatment objects
 * @path Users/{userID}/Treatments
 */
export async function getTreatments(userID) {
	return treatmentService.getTreatments(userID);
}

/**
 * Edits an existing treatment
 * @param {string} userID - The unique identifier for the user
 * @param {Object} document - Treatment data to update
 * @param {number|string} document.t - Timestamp used as document ID
 * @returns {Promise<void>}
 * @path Users/{userID}/Treatments/{document.t}
 */
export async function editTreatment(userID, document) {
	return treatmentService.editTreatment(userID, document);
}

/**
 * Deletes a treatment
 * @param {string} userID - The unique identifier for the user
 * @param {number|string} t - Timestamp used as document ID
 * @returns {Promise<void>}
 * @path Users/{userID}/Treatments/{t}
 */
export async function deleteTreatment(userID, t) {
	return treatmentService.deleteTreatment(userID, t);
}

/**
 * Creates a new treatment
 * @param {string} userID - The unique identifier for the user
 * @param {Object} treatment - Treatment data
 * @returns {Promise<boolean>} Success status
 */
export async function setNewTreatment(userID, treatment) {
	return treatmentService.setNewTreatment(userID, treatment);
}

/**
 * Creates a new birth document
 * @param {string} userID - The unique identifier for the user
 * @param {Object} birthDocument - Birth document data
 * @param {string} birthDocument.ID - Animal ID
 * @param {string} birthDocument.MAC - MAC address of the animal's device
 * @param {number} birthDocument.t - Timestamp used as document ID
 * @param {number|string} birthDocument.Living - Number of living births
 * @param {number|string} birthDocument.Dead - Number of dead births
 * @param {string} birthDocument.Comment - Additional comments
 * @returns {Promise<boolean>} Success status
 * @path Users/{userID}/Births/{birthDocument.t}
 */
export async function setNewBirthDocument(userID, birthDocument) {
	await db
		.collection("Users")
		.doc(userID)
		.collection("Births")
		.doc(birthDocument.t + "")
		.set(
			{
				ID: birthDocument.ID,
				MAC: birthDocument.MAC,
				t: birthDocument.t,
				Living: parseInt(birthDocument.Living),
				Dead: parseInt(birthDocument.Dead),
				Comment: birthDocument.Comment,
			},
			{ merge: true }
		)
		.catch((e) => {
			throw e;
		});

	return true;
}

/**
 * Edits an existing birth document
 * @param {string} userID - The unique identifier for the user
 * @param {Object} birthDocument - Birth document data to update
 * @param {string} birthDocument.ID - Animal ID
 * @param {number} birthDocument.t - Timestamp used as document ID
 * @param {number|string} birthDocument.Living - Number of living births
 * @param {number|string} birthDocument.Dead - Number of dead births
 * @param {string} [birthDocument.Comment=""] - Additional comments
 * @returns {Promise<boolean>} Success status
 * @path Users/{userID}/Births/{birthDocument.t}
 */
export async function editBirthDocument(userID, birthDocument) {
	birthDocument.Comment =
		birthDocument.Comment === undefined ? "" : birthDocument.Comment;

	await db
		.collection("Users")
		.doc(userID)
		.collection("Births")
		.doc(birthDocument.t + "")
		.set(
			{
				ID: birthDocument.ID,
				t: birthDocument.t,
				Living: parseInt(birthDocument.Living),
				Dead: parseInt(birthDocument.Dead),
				Comment: birthDocument.Comment,
			},
			{ merge: true }
		)
		.catch((e) => {
			throw e;
		});

	return true;
}

/**
 * Deletes a birth document
 * @param {string} userID - The unique identifier for the user
 * @param {Object} birthDocument - Birth document to delete
 * @param {number} birthDocument.t - Timestamp used as document ID
 * @returns {Promise<boolean>} Success status
 * @path Users/{userID}/Births/{birthDocument.t}
 */
export async function deleteBirthDocument(userID, birthDocument) {
	await db
		.collection("Users")
		.doc(userID)
		.collection("Births")
		.doc(birthDocument.t + "")
		.delete()
		.catch((e) => {
			throw e;
		});

	return true;
}

/**
 * Gets all birth documents for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of birth documents
 * @path Users/{userID}/Births
 */
export async function getAllBirthDocuments(userID) {
	let birthDocuments = [];

	await db
		.collection("Users")
		.doc(userID)
		.collection("Births")
		.get()
		.then((snapshot) => {
			snapshot.forEach((birthDocument) => {
				const data = birthDocument.data();
				// Ensure t (timestamp) is always present - fallback to document ID
				birthDocuments.push({ ...data, t: data.t || parseInt(birthDocument.id) });
			});
		})
		.catch((e) => {
			throw e;
		});

	return birthDocuments;
}

/**
 * Gets birth documents for a specific animal
 * @param {string} userID - The unique identifier for the user
 * @param {string} uid - The unique identifier for the animal
 * @returns {Promise<Array<Object>>} Array of birth documents for the animal
 * @path Users/{userID}/Births
 */
export async function getBirthDocumentsOfAnimal(userID, uid) {
	let birthDocuments = [];

	await db
		.collection("Users")
		.doc(userID)
		.collection("Births")
		.where("Uid", "==", uid)
		.get()
		.then((snapshot) => {
			snapshot.forEach((birthDocument) => {
				const data = birthDocument.data();
				// Ensure t (timestamp) is always present - fallback to document ID
				birthDocuments.push({ ...data, t: data.t || parseInt(birthDocument.id) });
			});
		})
		.catch((e) => {
			throw e;
		});

	return birthDocuments;
}

/**
 * Gets birth documents for multiple animals in a single query batch
 * @param {string} userID - The unique identifier for the user
 * @param {Array<string>} macs - Array of MAC addresses
 * @returns {Promise<Map<string, Array>>} Map of MAC -> birth records
 * @path Users/{userID}/Births
 */
export async function getBirthDocumentsForAnimals(userID, macs) {
	if (!macs || macs.length === 0) {
		return new Map();
	}

	try {
		// Firestore 'in' operator has a limit of 30 items, so we batch the queries
		const BATCH_SIZE = 30;
		const results = new Map();

		macs.forEach((mac) => results.set(mac, []));

		for (let i = 0; i < macs.length; i += BATCH_SIZE) {
			const batch = macs.slice(i, i + BATCH_SIZE);

			const snapshot = await db
				.collection("Users")
				.doc(userID)
				.collection("Births")
				.where("MAC", "in", batch)
				.get();

			snapshot.docs.forEach((doc) => {
				const data = doc.data();
				const record = { ...data, t: data.t || parseInt(doc.id) };
				if (!record.MAC) {
					return;
				}
				const existing = results.get(record.MAC) || [];
				existing.push(record);
				results.set(record.MAC, existing);
			});
		}

		return results;
	} catch (error) {
		console.error("[planerFirestore][getBirthDocumentsForAnimals] - Error:", error);
		throw error;
	}
}

/**
 * Creates a new insemination document
 * @param {string} userID - The unique identifier for the user
 * @param {Object} inseminationDocument - Insemination document data
 * @param {string} inseminationDocument.ID - Animal ID
 * @param {string} inseminationDocument.MAC - MAC address of the animal's device
 * @param {string|number} inseminationDocument.Tubes - Number of tubes used
 * @param {string} inseminationDocument.Boar - Boar identifier
 * @param {number} inseminationDocument.t - Timestamp used as document ID
 * @param {string} inseminationDocument.Comment - Additional comments
 * @returns {Promise<boolean>} Success status
 * @path Users/{userID}/Inseminations/{inseminationDocument.t}
 */
export async function setNewInseminationDocument(userID, inseminationDocument) {
	await db
		.collection("Users")
		.doc(userID)
		.collection("Inseminations")
		.doc(inseminationDocument.t + "")
		.set(
			{
				ID: inseminationDocument.ID,
				MAC: inseminationDocument.MAC,
				Tubes: inseminationDocument.Tubes,
				Boar: inseminationDocument.Boar,
				t: inseminationDocument.t,
				Comment: inseminationDocument.Comment,
			},
			{ merge: true }
		)
		.catch((e) => {
			throw e;
		});

	return true;
}

/**
 * Edits an existing insemination document
 * @param {string} userID - The unique identifier for the user
 * @param {Object} inseminationDocument - Insemination document data to update
 * @param {string} inseminationDocument.ID - Animal ID
 * @param {string|number} inseminationDocument.Tubes - Number of tubes used
 * @param {string} inseminationDocument.Boar - Boar identifier
 * @param {number} inseminationDocument.t - Timestamp used as document ID
 * @param {string} [inseminationDocument.Comment=""] - Additional comments
 * @returns {Promise<boolean>} Success status
 * @path Users/{userID}/Inseminations/{inseminationDocument.t}
 */
export async function editInseminationDocument(userID, inseminationDocument) {
	inseminationDocument.Comment =
		inseminationDocument.Comment === undefined
			? ""
			: inseminationDocument.Comment;

	await db
		.collection("Users")
		.doc(userID)
		.collection("Inseminations")
		.doc(inseminationDocument.t + "")
		.set(
			{
				ID: inseminationDocument.ID,
				Tubes: inseminationDocument.Tubes,
				Boar: inseminationDocument.Boar,
				t: inseminationDocument.t,
				Comment: inseminationDocument.Comment,
			},
			{ merge: true }
		)
		.catch((e) => {
			throw e;
		});

	return true;
}

/**
 * Deletes an insemination document
 * @param {string} userID - The unique identifier for the user
 * @param {Object} inseminationDocument - Insemination document to delete
 * @param {number} inseminationDocument.t - Timestamp used as document ID
 * @returns {Promise<boolean>} Success status
 * @path Users/{userID}/Inseminations/{inseminationDocument.t}
 */
export async function deleteInseminationDocument(userID, inseminationDocument) {
	await db
		.collection("Users")
		.doc(userID)
		.collection("Inseminations")
		.doc(inseminationDocument.t + "")
		.delete()
		.catch((e) => {
			throw e;
		});

	return true;
}

/**
 * Gets all insemination documents for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of insemination documents
 * @path Users/{userID}/Inseminations
 */
export async function getAllInsemenationDocuments(userID) {
	let inseminationDocuments = [];

	await db
		.collection("Users")
		.doc(userID)
		.collection("Inseminations")
		.get()
		.then((snapshot) => {
			snapshot.forEach((inseminationDocument) => {
				inseminationDocuments.push(inseminationDocument.data());
			});
		})
		.catch((e) => {
			throw e;
		});

	return inseminationDocuments;
}

/** Allowed MIME types for herd data uploads */
const ALLOWED_HERD_FILE_TYPES = [
	"application/pdf",
	"text/csv",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

/** Maximum file size: 10MB */
const MAX_HERD_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Uploads a file to Firebase Storage
 * @param {string} userID - The unique identifier for the user
 * @param {File} file - The file to upload
 * @returns {Promise<firebase.storage.Reference>} Storage reference to the uploaded file
 * @path Storage/HerdData/{userID}/{timestamp}_{sanitized}.pdf
 */
export async function uploadFile(userID, file) {
	if (!userID) {
		throw new Error("User ID is required");
	}
	if (!file) {
		throw new Error("File is required");
	}

	// Validate file type
	if (!ALLOWED_HERD_FILE_TYPES.includes(file.type)) {
		throw new Error("Invalid file type. Only PDF, CSV, and Excel files are allowed.");
	}

	// Validate file size
	if (file.size > MAX_HERD_FILE_SIZE) {
		throw new Error("File size exceeds 10MB limit.");
	}

	const storageRef = firebase.storage().ref();
	const timestamp = Date.now();
	const sanitizedFilename = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
	const fileRef = storageRef.child(
		`HerdData/${userID}/${timestamp}_${sanitizedFilename}.pdf`
	);

	await fileRef.put(file).catch((e) => {
		throw e;
	});

	return fileRef;
}

/**
 * Uploads integrated animals to Firestore
 * @param {string} userId - The unique identifier for the user
 * @param {Array<Object>} animals - Array of animal objects to upload
 * @param {string} animals[].ID - Animal ID used as document ID
 * @param {string} animals[].State - Animal state to be normalized
 * @returns {Promise<number>} Success status (0 for success)
 * @path Users/{userId}/IntegratedAnimals/{animal.ID}
 */
export async function uploadInteratedAnimals(userId, animals) {
	for (let i = 0; i < animals.length; i++) {
		let stateString = animals[i].State + "";

		if (stateString.includes("belegt")) {
			animals[i].State = "inseminated";
		} else if (stateString.includes("Zucht")) {
			animals[i].State = "gilt";
		} else if (stateString.includes("abgesetzt")) {
			animals[i].State = "weaned";
		} else if (stateString.includes("ferkel")) {
			animals[i].State = "pregnant";
		}

		await db
			.collection("Users")
			.doc(userId)
			.collection("IntegratedAnimals")
			.doc(animals[i].ID + "")
			.set(animals[i], { merge: true })
			.catch((e) => {
				throw e;
			});
	}

	return 0;
}

/**
 * Gets complete feeding data for a user within a time range
 * @param {string} userId - The unique identifier for the user
 * @param {number} start - Start timestamp
 * @param {number} end - End timestamp
 * @returns {Promise<Array<{Amount: any, Ventil: string, t: number}>>} Array of feeding data objects
 * @path Users/{userId}/WEDA/Ventiles
 * @path Users/{userId}/WEDA/Ventiles/{ventile.sidx}
 */
export async function getCompleteFeedingData(userId, start, end) {
	let ventiles = [];

	await db
		.collection("Users")
		.doc(userId)
		.collection("WEDA")
		.doc("Ventiles")
		.get()
		.then((snapshot) => {
			ventiles = snapshot.data().Ventiles;
		})
		.catch((e) => {
			throw e;
		});

	let feedingData = [];

	for (let i = 0; i < ventiles.length; i++) {
		await db
			.collection("Users")
			.doc(userId)
			.collection("WEDA")
			.doc("Ventiles")
			.collection(ventiles[i].sidx + "")
			//.where("t", ">=", start)
			//.where("t", "<=", end)
			.get()
			.then((snapshot) => {
				snapshot.forEach((doc) => {
					feedingData.push({
						Amount: doc.data().Data,
						Ventil: doc.data().Ventilname,
						t: doc.data().t,
					});
				});
			})
			.catch((e) => {
				throw e;
			});
	}

	return feedingData;
}

/**
 * Helper function to fetch animal data for a ventile
 * @param {Object} ventile - Ventile object to update with animal data
 * @param {number} ventile.index - Ventile index used to match location
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<void>}
 * @path Users/{userId}/Animals
 */
const handleVentileData = async (ventile, userId) => {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("Animals")
		.where("Location", "==", ventile.index + 1)
		.get();

	if (snapshot.docs.length > 0) {
		ventile.animal = snapshot.docs[0].data().ID;
		ventile.Group = snapshot.docs[0].data().Group;
	}
};

/**
 * Gets all ventiles with associated animal data
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of ventile objects with animal data
 * @path Users/{userId}/WEDA/Ventiles
 * @path Users/{userId}/Animals
 */
export async function getVentiles(userId) {
	let ventiles = [];

	await db
		.collection("Users")
		.doc(userId)
		.collection("WEDA")
		.doc("Ventiles")
		.get()
		.then((snapshot) => {
			ventiles = snapshot.data().Ventiles;
		})
		.catch((e) => {
			throw e;
		});

	if (ventiles.length > 0) {
		await Promise.all(
			ventiles.map((ventile) => handleVentileData(ventile, userId))
		);
	}

	return ventiles;
}

/**
 * Gets feeding data for a specific ventile
 * @param {string} userId - The unique identifier for the user
 * @param {Object} ventile - Ventile object
 * @param {string|number} ventile.sidx - Ventile index used as collection ID
 * @returns {Promise<Array<{Amount: any, Ventil: string, t: number}>>} Array of feeding data objects
 * @path Users/{userId}/WEDA/Ventiles/{ventile.sidx}
 */
export async function getFeedingDataOf(userId, ventile) {
	let feedingData = [];

	await db
		.collection("Users")
		.doc(userId)
		.collection("WEDA")
		.doc("Ventiles")
		.collection(ventile.sidx + "")
		.get()
		.then((snapshot) => {
			snapshot.forEach((doc) => {
				feedingData.push({
					Amount: doc.data().Data,
					Ventil: doc.data().Ventilname,
					t: doc.data().t,
				});
			});
		})
		.catch((e) => {
			throw e;
		});

	return feedingData;
}

/**
 * Gets saved medical categories for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of medical category objects
 * @path Users/{userId}/Account/Medical
 */
export async function getSavedMedicalCategories(userId) {
	let categories = [];

	await db
		.collection("Users")
		.doc(userId)
		.collection("Account")
		.doc("Medical")
		.get()
		.then((snapshot) => {
			if (
				snapshot.data() !== undefined &&
				snapshot.data().Medications !== undefined
			) {
				categories = snapshot.data().Medications;
			}
		})
		.catch((e) => {
			throw e;
		});

	return categories;
}

/**
 * Saves medical categories for a user
 * @param {string} userId - The unique identifier for the user
 * @param {Array<Object>} categories - Array of medical category objects
 * @returns {Promise<boolean>} Success status
 * @path Users/{userId}/Account/Medical
 */
export async function saveMedicalCategories(userId, categories) {
	await db
		.collection("Users")
		.doc(userId)
		.collection("Account")
		.doc("Medical")
		.set(
			{
				Medications: categories,
			},
			{ merge: true }
		)
		.catch((e) => {
			throw e;
		});

	return true;
}
